/**
 * Iter51 Etapa 5 — ClientOverview (single-source mockup-faithful client page).
 *
 * Layout pe același pattern ca SaleOverview (Iter51 Etapa 4):
 *   1. Breadcrumb (Clienți → Nume client)
 *   2. Hero header — Avatar inițiale + Nume + CNP/CUI + Județ/Loc + #Contracte
 *      + butoane Editează/Contactează (sau Salvează/Anulează în edit)
 *   3. 3 metric pills (Tip client / #Contracte / Verificat)
 *   4. 2-col grid: Identitate + Contact (iconițe per câmp)
 *   5. Adresă card
 *   6. Reprezentant legal (PJ only) — toggle FLAC-aware
 *   7. GDPR Consents (componenta existentă, păstrată)
 *   8. Linked sales shortcut
 *
 * Inline edit toggle (?edit=1):
 *   - „Editează" comută în mod edit pentru toate câmpurile editabile
 *   - „Salvează" PUT /api/clients/{id} cu diff partial (exclude_unset=True)
 *   - „Anulează" reset draft
 *
 * Câmpuri identitate (CNP / CUI) sunt READ-ONLY — modificarea lor se face din
 * formularul complet (ClientForm) cu validare de unicitate.
 */
import React, { useState, useMemo, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  ChevronRight, IdCard, MapPin, Phone, Mail, Home, Building, User, Edit,
  Save, X, ShieldCheck, FileText, Hash, Briefcase, Tag,
} from "lucide-react";
// Iter52 P14l — ClientConsents widget removed from UI per user decision.
// Data still kept in DB; Easy importer continues to populate consent flags.
// import { ClientConsents } from "./ClientConsents";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// =========================== helpers ===========================
const initials = (name) => {
  if (!name) return "—";
  return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0] || "").join("").toUpperCase();
};

// =========================== row primitives ===========================
const FieldRow = ({ icon: Icon, label, value, mono = false, badge = null, isEditing = false, editor = null }) => (
  <div className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-0">
    <span className="flex items-center gap-1.5 text-xs text-slate-500 whitespace-nowrap min-w-fit">
      {Icon && <Icon className="h-3.5 w-3.5 text-slate-400" />}
      {label}
    </span>
    {isEditing && editor ? (
      <div className="flex-1 max-w-[280px]">{editor}</div>
    ) : (
      <span className={`text-right text-sm font-medium text-slate-900 ${mono ? "font-mono text-xs" : ""}`}>
        {badge ? badge : (value || value === 0 ? value : <span className="text-slate-300 font-normal">—</span>)}
      </span>
    )}
  </div>
);

const SectionCard = ({ icon: Icon, title, children, testId, className = "", action = null }) => (
  <Card className={`shadow-sm border-slate-200 ${className}`} data-testid={testId}>
    <CardContent className="p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          {Icon && <Icon className="h-3.5 w-3.5" />}
          {title}
        </h3>
        {action}
      </div>
      <div className="divide-y divide-slate-100">{children}</div>
    </CardContent>
  </Card>
);

// =========================== verified contact badge ===========================
const VerifiedBadge = ({ verified, onClick, canEdit, testid }) => {
  if (verified) {
    return (
      <button
        type="button"
        onClick={canEdit ? onClick : undefined}
        disabled={!canEdit}
        className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-700 hover:bg-emerald-200 disabled:cursor-default"
        data-testid={testid}
        title={canEdit ? "Click pentru a anula verificarea" : "Verificat"}
      >
        <ShieldCheck className="h-3 w-3" /> verificat
      </button>
    );
  }
  if (!canEdit) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 rounded-full border border-slate-300 px-2 py-0.5 text-[10px] font-medium text-slate-500 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700"
      data-testid={testid}
    >
      <ShieldCheck className="h-3 w-3" /> marchează verificat
    </button>
  );
};

// =========================== main component ===========================
const ClientOverview = ({ client, onChange, canEdit = true, canValidateContact = false, flac = null }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isEditing, setIsEditing] = useState(searchParams.get("edit") === "1");
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (searchParams.get("edit") === "1" && !isEditing) setIsEditing(true);
  }, [searchParams, isEditing]);

  const toggleEdit = (on) => {
    setIsEditing(on);
    const next = new URLSearchParams(searchParams);
    if (on) next.set("edit", "1"); else next.delete("edit");
    setSearchParams(next, { replace: true });
  };

  // Init draft when entering edit
  useEffect(() => {
    if (!isEditing || !client) return;
    setDraft({
      // Identity
      first_name: client.first_name || "",
      last_name: client.last_name || "",
      full_name: client.full_name || "",
      company_name: client.company_name || "",
      id_series: client.id_series || "",
      representative_name: client.representative_name || "",
      representative_cnp: client.representative_cnp || "",
      representative_phone: client.representative_phone || "",
      representative_email: client.representative_email || "",
      legal_representative_name: client.legal_representative_name || "",
      legal_representative_cnp: client.legal_representative_cnp || "",
      legal_representative_id_series: client.legal_representative_id_series || "",
      legal_representative_role: client.legal_representative_role || "",
      // Contact
      phone_mobile: client.phone_mobile || "",
      phone_fixed: client.phone_fixed || "",
      email: client.email || "",
      // Address
      county: client.county || "",
      locality: client.locality || "",
      street: client.street || "",
      postal_code: client.postal_code || "",
    });
  }, [isEditing, client]);

  const handleSave = async () => {
    if (!client) return;
    setSaving(true);
    try {
      const diff = {};
      Object.entries(draft).forEach(([k, v]) => {
        const newV = (v ?? "").trim();
        const old = (client[k] ?? "").trim();
        if (newV !== old) diff[k] = newV || null;
      });
      // PF: derive full_name if first/last changed but full_name not explicit
      if ((diff.first_name !== undefined || diff.last_name !== undefined) && diff.full_name === undefined) {
        const fn = (draft.first_name || "").trim();
        const ln = (draft.last_name || "").trim();
        if (fn || ln) diff.full_name = `${ln} ${fn}`.trim();
      }
      if (Object.keys(diff).length === 0) {
        toast.info("Nicio modificare de salvat");
        toggleEdit(false);
        setSaving(false);
        return;
      }
      // Need to include client_type because backend PUT uses ClientCreate schema with default "PF"
      // and exclude_unset filters out unset fields → safe; but pass it explicitly to disambiguate.
      diff.client_type = client.client_type;
      await axios.put(`${API_URL}/clients/${client.id}`, diff);
      toast.success("Modificările au fost salvate");
      toggleEdit(false);
      onChange?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "flac_write_denied") {
        toast.error(`Câmpuri blocate de FLAC: ${detail.message || ""}`);
      } else if (detail?.code === "duplicate_identity") {
        toast.error(detail.message || "CNP/CUI duplicat");
      } else {
        toast.error(typeof detail === "string" ? detail : "Eroare la salvare");
      }
    } finally {
      setSaving(false);
    }
  };

  const toggleValidation = async (field) => {
    const newVal = !client[field];
    try {
      await axios.patch(`${API_URL}/clients/${client.id}/validate-contact`, { [field]: newVal });
      toast.success(newVal ? "Contact marcat verificat" : "Verificare anulată");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la actualizare");
    }
  };

  const isPF = client?.client_type === "PF";
  const displayName = useMemo(() => {
    if (!client) return "";
    return isPF
      ? (client.full_name || `${client.last_name || ""} ${client.first_name || ""}`.trim() || client.cnp || "Client")
      : (client.company_name || client.full_name || client.last_name || client.cui || "Client");
  }, [client, isPF]);

  if (!client) return null;

  const TextEditor = (field, placeholder = "") => (
    <Input
      value={draft[field] || ""}
      onChange={(e) => setDraft({ ...draft, [field]: e.target.value })}
      placeholder={placeholder}
      className="h-8 text-sm"
      data-testid={`edit-client-${field}`}
    />
  );

  const contractsCount = client.contracts_count || 0;
  const verifiedCount = (client.phone_validated ? 1 : 0) + (client.email_validated ? 1 : 0);

  return (
    <div className="space-y-3 max-w-5xl" data-testid="client-overview">
      {/* ===== 1. Breadcrumb ===== */}
      <nav className="flex items-center gap-1.5 text-xs text-slate-500" data-testid="client-breadcrumb">
        <Link to="/clients" className="hover:text-slate-700">Clienți</Link>
        <ChevronRight className="h-3 w-3" />
        <span className="text-slate-900 font-medium">{displayName}</span>
      </nav>

      {/* ===== 2. Hero header ===== */}
      <div className="flex items-start justify-between gap-4 flex-wrap" data-testid="client-hero">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`h-14 w-14 rounded-full ${isPF ? "bg-blue-50 text-blue-700" : "bg-amber-50 text-amber-700"} flex items-center justify-center text-base font-semibold flex-shrink-0`}>
            {initials(displayName)}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-semibold text-slate-900 truncate" data-testid="client-hero-name">{displayName}</h1>
              <Badge variant="outline" className="font-mono text-[10px]">{isPF ? "PF" : "PJ"}</Badge>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-0.5 flex-wrap">
              {(isPF ? client.cnp : client.cui) && (
                <>
                  <IdCard className="h-3.5 w-3.5" />
                  <span>{isPF ? "CNP" : "CUI"} {isPF ? client.cnp : client.cui}</span>
                </>
              )}
              {client.county && (
                <>
                  <span className="text-slate-300">·</span>
                  <MapPin className="h-3.5 w-3.5" />
                  <span>{client.county}{client.locality ? `, ${client.locality}` : ""}</span>
                </>
              )}
              <span className="text-slate-300">·</span>
              <FileText className="h-3.5 w-3.5" />
              <span>{contractsCount} contract{contractsCount === 1 ? "" : "e"}</span>
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap" data-testid="client-hero-actions">
          {!isEditing ? (
            <>
              {canEdit && (
                <Button variant="outline" size="sm" onClick={() => toggleEdit(true)} data-testid="client-edit-btn">
                  <Edit className="h-3.5 w-3.5 mr-1.5" /> Editează
                </Button>
              )}
              {client.phone_mobile && (
                <Button size="sm" asChild data-testid="client-contact-btn">
                  <a href={`tel:${client.phone_mobile}`}>
                    <Phone className="h-3.5 w-3.5 mr-1.5" /> Contactează
                  </a>
                </Button>
              )}
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={() => toggleEdit(false)} disabled={saving} data-testid="client-cancel-btn">
                <X className="h-3.5 w-3.5 mr-1.5" /> Anulează
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving} data-testid="client-save-btn">
                <Save className="h-3.5 w-3.5 mr-1.5" /> {saving ? "Se salvează..." : "Salvează"}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* ===== 3. Status pills ===== */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="client-status-pills">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <p className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Tip client</p>
            <div className="mt-1.5 flex items-center gap-1.5 text-sm font-semibold text-slate-900" data-testid="pill-client-type">
              {isPF ? <Home className="h-4 w-4 text-blue-600" /> : <Building className="h-4 w-4 text-amber-600" />}
              {isPF ? "Persoană Fizică (Residential)" : "Persoană Juridică (Business)"}
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <p className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Contracte asociate</p>
            <div className="mt-1.5 text-sm font-semibold text-slate-900" data-testid="pill-contracts">{contractsCount}</div>
            {contractsCount > 0 && (
              <Link to={`/sales?client_id=${client.id}`} className="text-[11px] text-blue-600 hover:underline mt-0.5 inline-block">Vezi vânzările →</Link>
            )}
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <p className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Contact verificat</p>
            <div className="mt-1.5 flex items-center gap-1.5 text-sm font-semibold text-slate-900" data-testid="pill-verified">
              <ShieldCheck className={`h-4 w-4 ${verifiedCount === 2 ? "text-emerald-600" : verifiedCount === 1 ? "text-amber-600" : "text-slate-300"}`} />
              {verifiedCount}/2 ({verifiedCount === 0 ? "neverificat" : verifiedCount === 1 ? "parțial" : "complet"})
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ===== 4. Identitate + Contact ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <SectionCard icon={isPF ? User : Building} title={isPF ? "Date personale" : "Date firmă"} testId="card-identity">
          {isPF ? (
            <>
              <FieldRow icon={User} label="Nume" value={client.last_name} isEditing={isEditing} editor={TextEditor("last_name")} />
              <FieldRow icon={User} label="Prenume" value={client.first_name} isEditing={isEditing} editor={TextEditor("first_name")} />
              <FieldRow icon={IdCard} label="CNP" value={client.cnp} mono />
              <FieldRow icon={Hash} label="Serie buletin" value={client.id_series} isEditing={isEditing} editor={TextEditor("id_series", "ex. AB123456")} />
            </>
          ) : (
            <>
              <FieldRow icon={Building} label="Nume firmă" value={client.company_name} isEditing={isEditing} editor={TextEditor("company_name")} />
              <FieldRow icon={IdCard} label="CUI" value={client.cui} mono />
              <FieldRow icon={User} label="Reprezentant" value={client.representative_name} isEditing={isEditing} editor={TextEditor("representative_name")} />
              <FieldRow icon={IdCard} label="CNP reprezentant" value={client.representative_cnp} mono isEditing={isEditing} editor={TextEditor("representative_cnp")} />
              <FieldRow icon={Phone} label="Telefon reprezentant" value={client.representative_phone} isEditing={isEditing} editor={TextEditor("representative_phone")} />
              <FieldRow icon={Mail} label="Email reprezentant" value={client.representative_email} isEditing={isEditing} editor={TextEditor("representative_email")} />
            </>
          )}
        </SectionCard>

        <SectionCard icon={Phone} title="Contact" testId="card-contact">
          <FieldRow
            icon={Phone}
            label="Telefon mobil"
            badge={!isEditing && client.phone_mobile ? (
              <span className="inline-flex items-center gap-2 justify-end">
                <span className="font-mono text-xs">{client.phone_mobile}</span>
                <VerifiedBadge verified={!!client.phone_validated} onClick={() => toggleValidation("phone_validated")} canEdit={canValidateContact} testid="phone-validated-badge" />
              </span>
            ) : null}
            value={client.phone_mobile}
            mono
            isEditing={isEditing}
            editor={TextEditor("phone_mobile", "ex. 0723123456")}
          />
          <FieldRow icon={Phone} label="Telefon fix" value={client.phone_fixed} mono isEditing={isEditing} editor={TextEditor("phone_fixed")} />
          <FieldRow
            icon={Mail}
            label="Email"
            badge={!isEditing && client.email ? (
              <span className="inline-flex items-center gap-2 justify-end">
                <a href={`mailto:${client.email}`} className="text-blue-600 hover:underline text-sm">{client.email}</a>
                <VerifiedBadge verified={!!client.email_validated} onClick={() => toggleValidation("email_validated")} canEdit={canValidateContact} testid="email-validated-badge" />
              </span>
            ) : null}
            value={client.email}
            isEditing={isEditing}
            editor={TextEditor("email", "client@exemplu.ro")}
          />
        </SectionCard>
      </div>

      {/* ===== 5. Adresă ===== */}
      <SectionCard icon={MapPin} title="Adresă" testId="card-address">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
          <div>
            <FieldRow icon={MapPin} label="Județ" value={client.county} isEditing={isEditing} editor={TextEditor("county", "ex. Dolj")} />
            <FieldRow icon={MapPin} label="Localitate" value={client.locality} isEditing={isEditing} editor={TextEditor("locality")} />
          </div>
          <div>
            <FieldRow icon={Home} label="Stradă" value={client.street} isEditing={isEditing} editor={TextEditor("street")} />
            <FieldRow icon={Tag} label="Cod poștal" value={client.postal_code} mono isEditing={isEditing} editor={TextEditor("postal_code")} />
          </div>
        </div>
        {client.address_raw && !isEditing && (
          <div className="pt-3 mt-2 border-t border-slate-100">
            <p className="text-[11px] uppercase tracking-wide text-slate-500">Adresă completă</p>
            <p className="mt-1 text-sm text-slate-600 italic" data-testid="d-address-raw">{client.address_raw}</p>
          </div>
        )}
      </SectionCard>

      {/* ===== 6. Reprezentant legal (PJ only) ===== */}
      {!isPF && (
        <SectionCard
          icon={Briefcase}
          title="Reprezentant legal"
          testId="card-legal-rep"
          action={flac?.isReadOnly?.("client.legal_representative_name") ? (
            <Badge variant="outline" className="text-[10px]">read-only</Badge>
          ) : null}
        >
          {/* FLAC drops hidden fields server-side; check 'in' on the client object */}
          {"legal_representative_name" in client && (
            <FieldRow icon={User} label="Nume reprezentant" value={client.legal_representative_name} isEditing={isEditing} editor={TextEditor("legal_representative_name")} />
          )}
          {"legal_representative_cnp" in client && (
            <FieldRow icon={IdCard} label="CNP reprezentant" value={client.legal_representative_cnp} mono isEditing={isEditing} editor={TextEditor("legal_representative_cnp")} />
          )}
          {"legal_representative_id_series" in client && (
            <FieldRow icon={Hash} label="Serie buletin" value={client.legal_representative_id_series} isEditing={isEditing} editor={TextEditor("legal_representative_id_series")} />
          )}
          {"legal_representative_role" in client && (
            <FieldRow icon={Briefcase} label="Funcție în firmă" value={client.legal_representative_role} isEditing={isEditing} editor={TextEditor("legal_representative_role")} />
          )}
          {!("legal_representative_name" in client) &&
            !("legal_representative_cnp" in client) &&
            !("legal_representative_id_series" in client) &&
            !("legal_representative_role" in client) && (
              <p className="text-xs italic text-slate-400 py-2" data-testid="legal-rep-hidden">
                Datele reprezentantului legal sunt ascunse pentru rolul tău.
              </p>
            )}
        </SectionCard>
      )}

      {/* ===== 7. Linked sales shortcut ===== */}
      {!isEditing && (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 text-slate-700">
              <FileText className="h-4 w-4" />
              <span className="text-sm">Vânzări asociate acestui client</span>
            </div>
            <Link to={`/sales?client_id=${client.id}`}>
              <Button variant="outline" size="sm" data-testid="view-sales-btn">
                Vezi vânzările ({contractsCount})
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* ===== 8. GDPR Consents — Iter52 P14l: read-only inline summary.
            Granular history widget + edit dialogs removed (per user decision 2026-02-18);
            Easy importer still populates flags; SF reports don't carry this data.
            We fetch the granular `client_consents` rows and show a compact badge per type. */}
      {!isEditing && <GdprConsentsReadOnly clientId={client.id} />}
    </div>
  );
};

// Iter52 P14l — Compact read-only summary of GDPR consents.
// Replaces the previous full <ClientConsents/> widget (timeline + edit dialogs).
const _CONSENT_LABELS = {
  electronic_invoice: "Factură electronică",
  marketing_email:    "Marketing e-mail",
  marketing_sms:      "Marketing SMS",
  marketing_phone:    "Marketing telefon",
  marketing_post:     "Marketing poștă",
  marketing_partner:  "Marketing partener",
  marketing_supplier: "Marketing furnizor",
};
const GdprConsentsReadOnly = ({ clientId }) => {
  const [consents, setConsents] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/clients/${clientId}/consents`);
        if (!cancelled) setConsents(r.data?.consents || []);
      } catch (_e) {
        if (!cancelled) setConsents([]);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId]);
  if (consents === null || consents.length === 0) return null;
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3" data-testid="client-gdpr-info">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        Consimțăminte GDPR
      </div>
      <div className="flex flex-wrap gap-1.5">
        {consents.map((cs) => {
          const isGranted = cs.status === "granted";
          const label = _CONSENT_LABELS[cs.consent_type] || cs.consent_type;
          const cls = isGranted
            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
            : "bg-slate-100 text-slate-600 border-slate-200";
          return (
            <Badge
              key={cs.id}
              variant="outline"
              className={`${cls} text-[11px]`}
              data-testid={`consent-${cs.consent_type}`}
              title={`${label}: ${isGranted ? "DA" : "NU"} · sursa: ${cs.source || "?"}`}
            >
              {label}: {isGranted ? "DA" : "NU"}
            </Badge>
          );
        })}
      </div>
    </div>
  );
};

export default ClientOverview;
